import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon, HelpCircle } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  trend?: {
    value: number;
    label: string;
  };
  icon?: React.ReactNode;
  tooltip?: string;
}

export function MetricCard({ title, value, trend, icon, tooltip }: MetricCardProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const getTrendIcon = () => {
    if (!trend) return null;
    
    if (trend.value > 0) {
      return <ArrowUpIcon className="h-4 w-4 text-muted-foreground" />;
    } else if (trend.value < 0) {
      return <ArrowDownIcon className="h-4 w-4 text-muted-foreground" />;
    } else {
      return <MinusIcon className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-1.5">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {tooltip && (
            <TooltipProvider>
              <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center"
                    onClick={() => setTooltipOpen(!tooltipOpen)}
                    onMouseEnter={() => setTooltipOpen(true)}
                    onMouseLeave={() => setTooltipOpen(false)}
                  >
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {icon && <div className="text-muted-foreground">{icon}</div>}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {value}
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-1">
            {getTrendIcon()}
            <p className="text-xs text-muted-foreground">
              {trend.label}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
